import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type { CreateOrderResponse, OrderPaymentFields } from "@/lib/api";

/**
 * Form pieces shared by every checkout page: the public CheckoutPage and the three
 * roster-initiated ones (Plan 2, Task 10). They live here, once, so the card rules
 * and the card-shaping rule cannot drift between pages that all charge a card.
 */

export const addressSchema = z.object({
  street: z.string().min(1, "Street is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().length(2, "Select a state"),
  zip: z.string().min(5, "ZIP code is required"),
});

/** The billing block IS the card billing address (decision 2026-07-30): standard
 *  checkout semantics, match the card statement. Every checkout page says so. */
export const BILLING_ADDRESS_DESCRIPTION =
  "Enter the billing address for the card you will pay with \u2014 the address on the card statement.";

export const ATTESTATION_REQUIRED_MESSAGE =
  "Confirm the attestation to order already-cleared badges.";

export function hasAttestedRow(employees: { product?: string }[]): boolean {
  return employees.some((employee) => employee.product === "badge_attested");
}

/** One employee row of a creation checkout. `product` is declared because zod
 *  strips unknown keys: a row whose product the schema does not name would
 *  silently submit as bcb. The "bcb" default lives in the form's defaultValues and
 *  in append(): a schema-level .default() makes the resolver's input type differ
 *  from the form's data type and breaks the useForm typing. */
export const employeeRowSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  product: z.enum(["bcb", "badge_attested"]),
});

export const cardSchema = z.object({
  cardNumber: z.string().optional(),
  expireMonth: z.string().optional(),
  expireYear: z.string().optional(),
  cvv: z.string().optional(),
  nameOnCard: z.string().optional(),
});

export type CardFields = z.infer<typeof cardSchema>;

/** The fields every page that takes a card carries. Card-only while ACH is
 *  deferred (2026-07-22) — see decisions/2026-07-22-defer-ach-card-only-rebuild.md. */
export const paymentFormShape = {
  billingAddress: addressSchema,
  paymentMethod: z.enum(["credit_card"]),
  card: cardSchema.optional(),
};

export interface PaymentFormValues {
  billingAddress: { street: string; city: string; state: string; zip: string };
  paymentMethod: "credit_card";
  card?: CardFields;
}

interface IssueSink {
  addIssue: (issue: { code: "custom"; message: string; path: (string | number)[] }) => void;
}

/** Card-field validation, for a schema's superRefine. Validation lives here and
 *  not in cardSchema so every issue carries the same message the page always had. */
export function addCardIssues(card: CardFields | undefined, ctx: IssueSink): void {
  const issue = (message: string, field: string): void =>
    ctx.addIssue({ code: "custom", message, path: ["card", field] });
  if (!card?.cardNumber || card.cardNumber.replace(/-/g, "").length < 13) {
    issue("Card number is required", "cardNumber");
  }
  if (!card?.expireMonth) issue("Expiration month is required", "expireMonth");
  if (!card?.expireYear) issue("Expiration year is required", "expireYear");
  if (!card?.cvv || card.cvv.length < 3) issue("CVV is required", "cvv");
}

/** The card as the API takes it: dashes stripped, a 4-digit year cut to 2. */
export function toOrderCard(card: CardFields): NonNullable<OrderPaymentFields["card"]> {
  const yearRaw = card.expireYear ?? "";
  return {
    cardNumber: (card.cardNumber ?? "").replace(/-/g, ""),
    expireMonth: card.expireMonth ?? "",
    expireYear: yearRaw.length === 4 ? yearRaw.slice(2) : yearRaw,
    cvv: card.cvv,
    nameOnCard: card.nameOnCard,
  };
}

/** The payment fields every order type carries, from the form and this
 *  submission's key. The billing address is the card's billing address (decision
 *  2026-07-30), so it rides the order exactly as the public checkout sends it. */
export function buildPaymentFields(
  data: PaymentFormValues,
  idempotencyKey: string,
): OrderPaymentFields {
  const fields: OrderPaymentFields = {
    paymentMethod: "credit_card",
    idempotencyKey,
    billingAddress: data.billingAddress,
  };
  if (data.card) fields.card = toOrderCard(data.card);
  return fields;
}

/** What a confirmed roster-initiated order does: refresh the roster and show the
 *  confirmation. It must never throw: a throw after a confirmed 201 would release
 *  the submit latch and invite a resubmit under a fresh key (the payment hook
 *  clears the key before calling this). */
export function useRosterCheckoutSuccess(): (result: CreateOrderResponse) => void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (result) => {
    void queryClient.invalidateQueries({ queryKey: ["roster"] });
    void navigate("/confirmation", {
      state: { order: result.order, employees: result.employees },
    });
  };
}

/** Invalid-submit handler: bring the first field in error into view, as the
 *  public checkout does. */
export function scrollToFirstError(): void {
  requestAnimationFrame(() => {
    const firstError = document.querySelector("[class*='border-red-500']");
    // jsdom has no scrollIntoView; real browsers always do.
    if (typeof firstError?.scrollIntoView === "function") {
      firstError.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });
}
